import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { CustomAction, ActionParameter } from '@/types/action';

// Async thunks for API calls
export const fetchActions = createAsyncThunk(
  'actions/fetchActions',
  async () => {
    // For now, return the mock data. In a real app, this would be an API call
    return [];
  }
);

interface ActionsState {
  items: CustomAction[];
  selectedIds: string[];
  loading: boolean;
  error: string | null;
  currentFolderId: string | null;
}

const initialState: ActionsState = {
  items: [
    {
      id: '1',
      name: 'Send Email Notification',
      description: 'Send automated email notifications to customers using SendGrid API',
      category: 'Communication',
      isActive: true,
      enabled: true,
      toolName: 'sendgrid_email_tool',
      endpoint: 'https://api.sendgrid.v3/mail/send',
      method: 'POST',
      parameters: [
        {
          id: 'param1',
          name: 'to_email',
          description: 'Recipient email address',
          type: 'string',
          required: true
        },
        {
          id: 'param2',
          name: 'subject',
          description: 'Email subject line',
          type: 'string',
          required: true
        },
        {
          id: 'param3',
          name: 'message_body',
          description: 'Email content body',
          type: 'string',
          required: true
        }
      ],
      headers: {
        'Authorization': 'Bearer {{api_key}}',
        'Content-Type': 'application/json'
      },
      createdAt: '2024-01-15T10:30:00Z',
      updatedAt: '2024-01-20T14:45:00Z',
      connectedChatbots: [
        { id: 'bot1', name: 'Customer Support Bot', type: 'chatbot' },
        { id: 'bot3', name: 'Sales Assistant Bot', type: 'chatbot' }
      ],
      createdBy: {
        id: 'user1',
        name: 'John Smith',
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face'
      }
    },
    {
      id: '2',
      name: 'Create Support Ticket',
      description: 'Automatically create support tickets in Zendesk when issues are detected',
      category: 'Support',
      isActive: true,
      enabled: true,
      toolName: 'zendesk_ticket_tool',
      endpoint: 'https://api.zendesk.com/api/v2/tickets.json',
      method: 'POST',
      parameters: [
        {
          id: 'param4',
          name: 'ticket_subject',
          description: 'Subject of the support ticket',
          type: 'string',
          required: true
        },
        {
          id: 'param5',
          name: 'description',
          description: 'Detailed description of the issue',
          type: 'string',
          required: true
        },
        {
          id: 'param6',
          name: 'priority',
          description: 'Ticket priority level',
          type: 'string',
          required: false,
          options: ['low', 'normal', 'high', 'urgent']
        },
        {
          id: 'param7',
          name: 'requester_email',
          description: 'Email of the person requesting support',
          type: 'string',
          required: true
        }
      ],
      headers: {
        'Authorization': 'Basic {{encoded_credentials}}',
        'Content-Type': 'application/json'
      },
      createdAt: '2024-01-10T09:15:00Z',
      updatedAt: '2024-01-18T16:20:00Z',
      connectedChatbots: [
        { id: 'bot2', name: 'Technical Support Bot', type: 'chatbot' }
      ],
      createdBy: {
        id: 'user2',
        name: 'Sarah Wilson',
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face'
      }
    },
    {
      id: '3',
      name: 'Update CRM Contact',
      description: 'Update customer information in Salesforce CRM system',
      category: 'CRM',
      isActive: true,
      enabled: false,
      toolName: 'salesforce_contact_tool',
      endpoint: 'https://api.salesforce.com/services/data/v55.0/sobjects/Contact',
      method: 'PATCH',
      parameters: [
        {
          id: 'param8',
          name: 'contact_id',
          description: 'Salesforce Contact ID',
          type: 'string',
          required: true
        },
        {
          id: 'param9',
          name: 'first_name',
          description: 'Contact first name',
          type: 'string',
          required: false
        },
        {
          id: 'param10',
          name: 'last_name',
          description: 'Contact last name',
          type: 'string',
          required: false
        },
        {
          id: 'param11',
          name: 'email',
          description: 'Contact email address',
          type: 'string',
          required: false
        },
        {
          id: 'param12',
          name: 'phone',
          description: 'Contact phone number',
          type: 'string',
          required: false
        }
      ],
      headers: {
        'Authorization': 'Bearer {{sf_access_token}}',
        'Content-Type': 'application/json'
      },
      createdAt: '2024-01-12T11:00:00Z',
      updatedAt: '2024-01-19T13:30:00Z',
      connectedChatbots: [
        { id: 'bot1', name: 'Customer Support Bot', type: 'chatbot' },
        { id: 'bot4', name: 'Lead Qualification Bot', type: 'chatbot' }
      ],
      createdBy: {
        id: 'user3',
        name: 'Mike Johnson',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face'
      }
    },
    {
      id: '4',
      name: 'Slack Notification',
      description: 'Send notifications to Slack channels for team updates',
      category: 'Communication',
      isActive: true,
      enabled: true,
      toolName: 'slack_webhook_tool',
      endpoint: 'https://hooks.slack.com/services/webhook',
      method: 'POST',
      parameters: [
        {
          id: 'param13',
          name: 'channel',
          description: 'Slack channel to send message to',
          type: 'string',
          required: true
        },
        {
          id: 'param14',
          name: 'message',
          description: 'Message content to send',
          type: 'string',
          required: true
        },
        {
          id: 'param15',
          name: 'username',
          description: 'Bot username for the message',
          type: 'string',
          required: false
        }
      ],
      headers: {
        'Content-Type': 'application/json'
      },
      createdAt: '2024-01-14T15:45:00Z',
      updatedAt: '2024-01-21T10:15:00Z',
      connectedChatbots: [
        { id: 'copilot1', name: 'Sales Copilot', type: 'copilot' },
        { id: 'copilot2', name: 'Support Copilot', type: 'copilot' }
      ],
      createdBy: {
        id: 'user1',
        name: 'John Smith',
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face'
      }
    },
    {
      id: '5',
      name: 'Payment Processing',
      description: 'Process payments through Stripe API integration',
      category: 'Finance',
      isActive: true,
      enabled: true,
      toolName: 'stripe_payment_tool',
      endpoint: 'https://api.stripe.com/v1/payment_intents',
      method: 'POST',
      parameters: [
        {
          id: 'param16',
          name: 'amount',
          description: 'Payment amount in cents',
          type: 'number',
          required: true
        },
        {
          id: 'param17',
          name: 'currency',
          description: 'Currency code (e.g., usd, eur)',
          type: 'string',
          required: true,
          options: ['usd', 'eur', 'gbp', 'cad']
        },
        {
          id: 'param18',
          name: 'customer_id',
          description: 'Stripe customer ID',
          type: 'string',
          required: false
        },
        {
          id: 'param19',
          name: 'description',
          description: 'Payment description',
          type: 'string',
          required: false
        }
      ],
      headers: {
        'Authorization': 'Bearer {{stripe_secret_key}}',
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      createdAt: '2024-01-16T12:20:00Z',
      updatedAt: '2024-01-22T09:40:00Z',
      connectedChatbots: [
        { id: 'bot3', name: 'Sales Assistant Bot', type: 'chatbot' },
        { id: 'copilot3', name: 'Finance Copilot', type: 'copilot' }
      ],
      createdBy: {
        id: 'user4',
        name: 'Emma Davis',
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=40&h=40&fit=crop&crop=face'
      }
    }
  ],
  selectedIds: [],
  loading: false,
  error: null,
  currentFolderId: null,
};

const actionsSlice = createSlice({
  name: 'actions',
  initialState,
  reducers: {
    addAction: (state, action: PayloadAction<CustomAction>) => {
      state.items.push(action.payload);
    },
    updateAction: (state, action: PayloadAction<Partial<CustomAction> & { id: string }>) => {
      const index = state.items.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload };
      }
    },
    deleteAction: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.id !== action.payload);
      state.selectedIds = state.selectedIds.filter(id => id !== action.payload);
    },
    duplicateAction: (state, action: PayloadAction<string>) => {
      const originalAction = state.items.find(item => item.id === action.payload);
      if (originalAction) {
        const newAction: CustomAction = {
          ...originalAction,
          id: `${originalAction.id}_copy_${Date.now()}`,
          name: `${originalAction.name} (Copy)`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        state.items.push(newAction);
      }
    },
    toggleActionSelection: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.selectedIds.includes(id)) {
        state.selectedIds = state.selectedIds.filter(selectedId => selectedId !== id);
      } else {
        state.selectedIds.push(id);
      }
    },
    clearSelection: (state) => {
      state.selectedIds = [];
    },
    bulkDeleteActions: (state, action: PayloadAction<string[]>) => {
      state.items = state.items.filter(item => !action.payload.includes(item.id));
      state.selectedIds = [];
    },
    bulkMoveActions: (state, action: PayloadAction<{ actionIds: string[]; folderId: string | null }>) => {
      // Implementation for moving actions to folders would go here
      state.selectedIds = [];
    },
    bulkUpdateActions: (state, action: PayloadAction<{ actionIds: string[]; updates: Partial<CustomAction> }>) => {
      const { actionIds, updates } = action.payload;
      state.items = state.items.map(item => 
        actionIds.includes(item.id) ? { ...item, ...updates } : item
      );
      state.selectedIds = [];
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setCurrentFolderId: (state, action: PayloadAction<string | null>) => {
      state.currentFolderId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActions.fulfilled, (state, action) => {
        state.loading = false;
        // For now, keep using the mock data. In a real app, this would replace state.items
        // state.items = action.payload;
      })
      .addCase(fetchActions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch actions';
      });
  },
});

export const {
  addAction,
  updateAction,
  deleteAction,
  duplicateAction,
  toggleActionSelection,
  clearSelection,
  bulkDeleteActions,
  bulkMoveActions,
  bulkUpdateActions,
  setLoading,
  setError,
  setCurrentFolderId,
} = actionsSlice.actions;

export const selectAllActions = (state: { actions: ActionsState }) => state.actions.items;
export const selectActions = selectAllActions; // Alias for compatibility
export const selectActionById = (state: { actions: ActionsState }, id: string) => 
  state.actions.items.find(action => action.id === id);
export const selectSelectedActionIds = (state: { actions: ActionsState }) => state.actions.selectedIds;
export const selectActionsLoading = (state: { actions: ActionsState }) => state.actions.loading;
export const selectActionsError = (state: { actions: ActionsState }) => state.actions.error;

// Export the reducer with the correct name
export default actionsSlice.reducer;
